import { themeV2 } from '@features/executive-report/domain/theme-v2';
import { slideMasters } from '@features/executive-report/utils/slide-masters';
import {
  chartOptionsToBase64Svg,
  ShapeProps,
  Slide,
  TextProps,
} from '@features/powerpoint';
import { buildNavbarText } from './components/navbar-texts';
import { navbarSections } from '@features/executive-report/subdomains/visitation/domain/navbar-sections';
import { formatters, ValueInput } from '@utils/formattersV2';
import { formatWithSign } from '@features/executive-report/utils/format-with-sign';
import {
  salesOptimizationChart,
  SalesOptimizationChartOptions,
} from '@features/executive-report/configuration/sales-optimization-chart';

const SECTION_X_OFFSET = 6.38;

const PPT_CHART_WIDTH = 5.84;
const PPT_CHART_HEIGHT = 3.55;
const PPT_CHART_SCALING_FACTOR = 100;
const PX_CHART_WIDTH = PPT_CHART_WIDTH * PPT_CHART_SCALING_FACTOR;
const PX_CHART_HEIGHT = PPT_CHART_HEIGHT * PPT_CHART_SCALING_FACTOR;

const salesFormatterOverride = (value: ValueInput) =>
  formatters.currency(value, { decimals: 0, full: true });

type BuildCampaignSummaryTextsProps = {
  timing: 'Middle' | 'End';
  lift: number;
  totalIncrementality: number;
  index: number;
};

const buildCampaignSummaryTexts = (
  props: BuildCampaignSummaryTextsProps
): TextProps[] => {
  const { timing, lift, totalIncrementality } = props;
  return [
    {
      text: [
        {
          text: timing.toLocaleUpperCase(),
          options: {
            bold: true,
          },
        },
        {
          text: ' OF CAMPAIGN',
        },
      ],
      options: {
        color: themeV2.colors.white,
        fontSize: 14,
        h: 0.34,
        w: 2.59,
        x: 0.74 + props.index * SECTION_X_OFFSET,
        y: 1.83,
      },
    },
    {
      text: 'LIFT',
      options: {
        fontSize: 10,
        bold: true,
        color: themeV2.colors.white,
        valign: 'middle',
        h: 0.27,
        w: 0.55,
        x: 0.9 + props.index * SECTION_X_OFFSET,
        y: 2.83,
      },
    },
    {
      text: formatters.currency(lift, { full: true, decimals: 0 }),
      options: {
        fontSize: 20,
        bold: true,
        color: themeV2.colors.inmarketGreen,
        valign: 'middle',
        h: 0.44,
        w: 2.17,
        x: 1.45 + props.index * SECTION_X_OFFSET,
        y: 2.74,
      },
    },
    {
      text: formatters.lift(totalIncrementality, { decimals: 1 }),
      options: {
        fontSize: 20,
        color: themeV2.colors.inmarketGreen,
        valign: 'middle',
        h: 0.44,
        w: 1.24,
        x: 3.44 + props.index * SECTION_X_OFFSET,
        y: 2.74,
      },
    },
    {
      text: 'TOTAL INCREMENTALITY',
      options: {
        fontSize: 8,
        color: themeV2.colors.white,
        valign: 'middle',
        h: 0.37,
        w: 1.33,
        x: 4.57 + props.index * SECTION_X_OFFSET,
        y: 2.78,
      },
    },
  ];
};

const buildCampaignSummaryShapes = (index: number): ShapeProps[] => {
  return [
    {
      shapeName: 'roundRect',
      options: {
        rectRadius: 0.3,
        fill: { type: 'solid', color: themeV2.colors.metricBlue },
        h: 5.49,
        w: 5.88,
        x: 0.54 + index * SECTION_X_OFFSET,
        y: 1.51,
      },
    },
    {
      shapeName: 'line',
      options: {
        line: { color: themeV2.colors.inmarketGreen, width: 1.5 },
        h: 0,
        w: 4.2,
        x: 0.54 + index * SECTION_X_OFFSET,
        y: 2.46,
      },
    },
    {
      shapeName: 'roundRect',
      options: {
        line: { color: themeV2.colors.inmarketGreen, width: 0.75 },
        h: 0.56,
        w: 5.11,
        x: 0.75 + index * SECTION_X_OFFSET,
        y: 2.68,
      },
    },
  ];
};

const campaignSummaryToChartData = (
  campaignSummary: CampaignSummary
): SalesOptimizationChartOptions['data'] => [
  {
    sales: campaignSummary.penetrationSales,
    lift: campaignSummary.penetrationLift,
    label: 'Penetration',
    salesFormatterOverride,
  },
  {
    sales: campaignSummary.transactionSizeSales,
    lift: campaignSummary.transactionSizeLift,
    label: 'Transaction Size',
    salesFormatterOverride,
  },
  {
    sales: campaignSummary.transactionRateSales,
    lift: campaignSummary.transactionRateLift,
    label: 'Transaction Rate',
    salesFormatterOverride,
  },
];

type CampaignSummary = {
  lift: number;
  totalIncrementality: number;
  transactionRateSales: number;
  transactionRateLift: number;
  transactionSizeSales: number;
  transactionSizeLift: number;
  penetrationSales: number;
  penetrationLift: number;
};

type SlideProps = {
  midCampaignSummary: CampaignSummary | null;
  endCampaignSummary: CampaignSummary;
};

export const optimizationChannelsSlide = (props: SlideProps): Slide => {
  const campaignSummaryTextsProps: Omit<
    BuildCampaignSummaryTextsProps,
    'index'
  >[] = [
    {
      timing: 'Middle',
      lift: props.midCampaignSummary?.lift ?? 0,
      totalIncrementality: props.midCampaignSummary?.totalIncrementality ?? 0,
    },
    {
      timing: 'End',
      lift: props.endCampaignSummary.lift,
      totalIncrementality: props.endCampaignSummary.totalIncrementality,
    },
  ];

  return {
    slideMaster: slideMasters.light,
    color: themeV2.colors.metricBlue,
    shapes: campaignSummaryTextsProps.flatMap((_, index) =>
      buildCampaignSummaryShapes(index)
    ),
    images: [
      ...(props.midCampaignSummary
        ? [
            {
              data: chartOptionsToBase64Svg({
                chartOptions: salesOptimizationChart({
                  data: campaignSummaryToChartData(props.midCampaignSummary),
                }),
                width: PX_CHART_WIDTH,
                height: PX_CHART_HEIGHT,
              }),
              h: PPT_CHART_HEIGHT,
              w: PPT_CHART_WIDTH,
              x: 0.82,
              y: 3.38,
            },
          ]
        : []),
      {
        data: chartOptionsToBase64Svg({
          chartOptions: salesOptimizationChart({
            data: campaignSummaryToChartData(props.endCampaignSummary),
          }),
          width: PX_CHART_WIDTH,
          height: PX_CHART_HEIGHT,
        }),
        h: PPT_CHART_HEIGHT,
        w: PPT_CHART_WIDTH,
        x: 0.82 + SECTION_X_OFFSET,
        y: 3.38,
      },
    ],
    texts: [
      buildNavbarText(navbarSections, 'Summary'),
      {
        text: 'Optimization Channels',
        options: {
          fontSize: 36,
          bold: true,
          h: 0.75,
          w: 12.67,
          x: 0.33,
          y: 0.59,
        },
      },
      {
        text: 'Sales Lift',
        options: {
          fontSize: 18,
          bold: true,
          valign: 'middle',
          h: 0.4,
          w: 1.4,
          x: 9,
          y: 0.76,
        },
      },
      {
        text: formatWithSign(
          props.endCampaignSummary.totalIncrementality,
          value => formatters.lift(value, { decimals: 1 })
        ),
        options: {
          fontSize: 36,
          bold: true,
          color: themeV2.colors.inmarketGreen,
          valign: 'middle',
          align: 'right',
          h: 0.71,
          w: 2.5,
          x: 10,
          y: 0.63,
        },
      },
      ...campaignSummaryTextsProps.flatMap((props, index) =>
        buildCampaignSummaryTexts({ ...props, index })
      ),
    ],
  };
};
